import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Sale, SaleCorrection, UserRole, Vehicle, VehicleAssessment } from "../../system/Domain";
import SidebarGroup from "../../Components/Sidebar/SidebarGroup";
import Sidebar from "../../Components/Sidebar/Sidebar";
import SidebarButton from "../../Components/Sidebar/SidebarButton";
import axios from "axios";
import useUser from "../../system/useUser";
import useAction from "../../system/useAction";
import { useHistory } from "react-router";
import ask from "../../Dialogs/ask";
import { RemarketingActions } from "./RemarketingLayout";
import Tile from "../../Components/Tiles/Tile";
import TileContent from "../../Components/Tiles/TileContent";
import { Box } from "@mui/material";
import ListItemChips from "../../Components/ListItemChips";
import ListItemChip from "../../Components/ListItemChip";
import { Cancel, CancelScheduleSend, Check } from "@mui/icons-material";
import { setUserMessage } from "../../system/reducers/userMessage/actions";
import { useDispatch } from "react-redux";
import showMessage from "../../Dialogs/showMessage";
import selectVehicleAssessment from "../../Dialogs/selectVehicleAssessment";
import confirm from "../../Dialogs/confirm";
import { LeasmanSyncStatus } from "./LeasmanSyncTile";

const LeasmanSyncIndicator = ({ vehicle, tooLong }: { vehicle: Vehicle, tooLong: boolean }) => {
	const isLeasmanVehicle = !!vehicle.remarketing.leasmanVehicleId;

	const referenceCreated = vehicle.remarketing.leasmanContract?.dateReferenceCreated;
	const color = !referenceCreated ? "error" : (tooLong ? "secondary" : "success");
	const label = tooLong ? "Synchronisation notwendig" : "Synchronisation erfolgreich";
	const emailPresent = !!vehicle.remarketing.leasmanContract?.customer?.email?.trim();

	return (
		<Tile>
			<TileContent>
				<LeasmanSyncStatus vehicle={vehicle} />
				<Box mt={1}>
					<ListItemChips>
						{isLeasmanVehicle && (
							<ListItemChip
								label={label}
								icon={!tooLong ? <Check /> : <Cancel />}
								color={color}
							/>
						)}
						{!emailPresent && (
							<ListItemChip
								tooltip="Bitte die Email im Kunden in Leasman pflegen"
								label="Keine Email hinterlegt"
								icon={<CancelScheduleSend />}
								color="error"
							/>
						)}
						{!isLeasmanVehicle && (
							<ListItemChip
								label="Kein Leasmanfahrzeug"
								icon={<Cancel />}
								color="primary"
							/>
						)}
					</ListItemChips>
				</Box>
			</TileContent>
		</Tile>
	);
};

interface Props {
	vehicle: Vehicle
	setVehicle: Dispatch<SetStateAction<Vehicle | undefined>>
	sale?: Sale
	setSale: Dispatch<SetStateAction<Sale | undefined>>
	completedActions: RemarketingActions[]
	mode: RemarketingActions
	setMode: Dispatch<SetStateAction<RemarketingActions>>
	canAccessFinalInvoice: boolean
	saveFinalInvoice: () => void
	isLoading: boolean
	setIsLoading: Dispatch<SetStateAction<boolean>>
	canTransferValidation: boolean
	disableValidation: boolean
	tooLong: boolean
}

const RemarketingSidebar = (props: Props) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const [, , hasRole, , hasAnyRole] = useUser();

	const [create, isCreating] = useAction(async () => {
		await axios.post<Sale>(`/api/sales`, { vehicleId: props.vehicle.id });
		const { data } = await axios.get<Vehicle>(`/api/vehicles/${props.vehicle.id}`);
		props.setVehicle(data);
	});

	const [cancel, isCancelling] = useAction(async () => {
		if (!props.vehicle.remarketing.saleId) {
			return;
		}

		const answer = await ask("Soll der Verkauf sicher unwiderruflich gelöscht werden?");
		if (answer.yes) {
			await axios.delete<Sale>(`/api/sales/${props.vehicle.remarketing.saleId}`);
			const { data } = await axios.get<Vehicle>(`/api/vehicles/${props.vehicle.id}`);
			props.setVehicle(data);
		}
	});

	const [synchronize] = useAction(async () => {
		if (!props.vehicle) return;

		props.setIsLoading(true);

		try {
			const { data } = await axios.post<Vehicle>(`/api/vehicles/${props.vehicle.id}/remarketing/validation/synchronize`);

			props.setVehicle(data);

			const emailPresent = !!data.remarketing.leasmanContract?.customer?.email?.trim();
			if (!emailPresent) {
				await showMessage({
					title: "Kundenemail nicht gepflegt",
					message: `Die Emailadresse des Kunden ist nicht gepflegt. Für nachfolgende Prozesse ist diese notwendig. Bitte veranlassen Sie die Pflege in Leasman und synchronisieren sie erneut.`
				});
			}
		} finally {
			props.setIsLoading(false);
		}
	});

	const [correction, setCorrection] = useState<SaleCorrection>();

	useEffect(() => {
		if (!props.sale) {
			setCorrection(undefined);
			return;
		}

		const correction = props.sale.corrections.find(c => c.status === "Created");
		setCorrection(correction);
	}, [props.sale]);

	const [createCorrection, isCreatingCorrection] = useAction(async () => {
		if (correction) {
			return;
		}

		const { data } = await axios.post<Sale>(`/api/sales/${props.vehicle.remarketing.saleId}/corrections`);
		props.setSale(data);
	});

	const [cancelCorrection, isCancellingCorrection] = useAction(async () => {
		if (!correction) {
			return;
		}

		const { data } = await axios.post<Sale>(`/api/sales/${props.vehicle.remarketing.saleId}/corrections/${correction.id}/cancel`);
		props.setSale(data);
	});

	const [applyCorrection, isApplyingCorrection] = useAction(async () => {
		if (!correction) {
			return;
		}

		const { data } = await axios.post<Sale>(`/api/sales/${props.vehicle.remarketing.saleId}/corrections/${correction.id}/apply`);
		props.setSale(data);
	});

	const [resetValidation, isResettingValidation] = useAction(async () => {
		if (!props.vehicle) return;

		const { data: assessments } = await axios.get<VehicleAssessment[]>(`/api/assessments?vin=${props.vehicle.vin}`);

		let assessmentId: string | null | undefined;

		if (assessments.length > 1) {
			assessmentId = await selectVehicleAssessment(
				assessments,
				props.vehicle.remarketing?.assessment?.assessmentId
			);
			if (assessmentId === null) return;
		} else {
			assessmentId = assessments[0]?.id ?? props.vehicle.remarketing?.assessment?.assessmentId;

			const result = await confirm(
				"Sind Sie sicher, dass sie die Validation zurücksetzen wollen?",
				"Warnung"
			);

			if (!result.ok)
				return;
		}

		const { data } = await axios.post<Vehicle>(
			`/api/vehicles/${props.vehicle.id}/remarketing/validation/reset`,
			{ vehicleAssessmentId: assessmentId }
		);

		props.setVehicle(data);

		dispatch(setUserMessage("Validierung zurückgesetzt", "success"));
	});

	const transferValidation = async () => {
		if (!props.vehicle) return;

		const emailPresent = !!props.vehicle.remarketing.leasmanContract?.customer?.email?.trim();

		if (emailPresent) {
			const { data } = await axios.post<Vehicle>(`/api/vehicles/${props.vehicle.id}/remarketing/validation/transfer`);

			props.setVehicle(data);

			dispatch(setUserMessage(`Übertragung erfolgreich`, "success"));
		}
		else {
			await showMessage({
				title: "Kundenemail nicht gepflegt",
				message: `Die Emailadresse des Kunden ist nicht gepflegt. Für nachfolgende Prozesse ist diese notwendig. Bitte veranlassen Sie die Pflege in Leasman und synchronisieren sie erneut. Übertragung nach Leasman ist so nicht erlaubt.`
			});
		}
	};

	const canTransferLeasmanOnly = hasRole(UserRole.ValidationAdmin) && !!props.vehicle.remarketing.validation?.dateTransferred;

	const [transferLeasmanOnly, isTransferringLeasmanOnly] = useAction(async () => {
		if (!props.vehicle) return;

		const result = await confirm(
			"Hiermit wird die Endabrechnung erneut - ausschließlich - nach Leasman übertragen. Es werden keine E-Mails versendet und keine weiteren Prozesse ausgelöst. Fortfahren?",
			"Nur nach Leasman übertragen"
		);

		if (!result.ok) return;

		const { data } = await axios.post<Vehicle>(`/api/vehicles/${props.vehicle.id}/remarketing/validation/transfer-leasman-only`);

		props.setVehicle(data);

		dispatch(setUserMessage("Leasmanübertragung erfolgreich", "success"));
	});

	const vehicleStatusAllowsReset = props.vehicle.remarketing?.validation?.status !== "Started";

	const hasValidPictureSet = props.vehicle.pictureSets?.some(ps => !ps.dateDeleted && !ps.dateRejected) ?? false;

	return (
		<Sidebar>
			<SidebarGroup>
				{!props.vehicle.remarketing.saleId && (
					<SidebarButton
						label="Verkauf anlegen"
						color="primary"
						disabled={isCreating || !props.vehicle.remarketing.isReadyForSale || !hasValidPictureSet}
						onClick={create}
					/>
				)}
				{!!props.vehicle.remarketing.saleId && (
					<SidebarButton
						label="Zum Verkauf"

						onClick={() => history.push(`/sales/${props.vehicle.remarketing.saleId}`)}
					/>
				)}
				{hasRole(UserRole.AldAdmin) && !!props.vehicle.remarketing.saleId && (
					<SidebarButton
						label="Verkauf stornieren"
						color="secondary"
						disabled={isCancelling}
						onClick={cancel}
					/>
				)}
			</SidebarGroup>
			<SidebarGroup>
				<SidebarButton
					label="Informationen"
					onClick={() => props.setMode("Information")}
					color={props.mode === "Information" ? "primary" : "inherit"}
				/>
				<SidebarButton
					label="Validierung"
					disabled={!props.canAccessFinalInvoice}
					onClick={() => props.setMode("Endabrechnung")}
					color={props.mode === "Endabrechnung" ? "primary" : "inherit"}
				/>
				<SidebarButton
					label="Verkaufsvorbereitung"
					onClick={() => props.setMode("Verkaufsvorbereitung")}
					color={props.mode === "Verkaufsvorbereitung" ? "primary" : "inherit"}
				/>
			</SidebarGroup>
			{props.mode === "Endabrechnung" && (
				<SidebarGroup>
					<SidebarButton
						label="Endabrechnung übertragen"
						disabled={!props.canTransferValidation}
						onClick={transferValidation}
						color="primary"
					/>
					{canTransferLeasmanOnly && (
						<SidebarButton
							label="Nur nach Leasman übertragen"
							disabled={isTransferringLeasmanOnly}
							onClick={transferLeasmanOnly}
							color="secondary"
						/>
					)}
					<SidebarButton
						label="Speichern"
						onClick={props.saveFinalInvoice}
						disabled={props.disableValidation}
						color="secondary"
					/>
					{(hasRole(UserRole.ValidationAdmin) || hasRole(UserRole.ValidationManager)) && props.vehicle.remarketing.validation && (
						<SidebarButton
							label="Validation zurücksetzen"
							disabled={isResettingValidation || !vehicleStatusAllowsReset}
							onClick={resetValidation}
							color="secondary"
						/>
					)}
				</SidebarGroup>
			)}
			<SidebarGroup>
				{!!props.sale && !correction && (
					<SidebarButton
						label="Korrektur starten"
						disabled={isCreatingCorrection}
						onClick={createCorrection}
					/>
				)}

				{!!props.sale && !!correction && (
					<SidebarButton
						label="Korrektur bestätigen"
						color="primary"
						disabled={isApplyingCorrection}
						onClick={applyCorrection}
					/>
				)}

				{!!props.sale && !!correction && (
					<SidebarButton
						label="Korrektur abbrechen"
						color="secondary"
						disabled={isCancellingCorrection}
						onClick={cancelCorrection}
					/>
				)}

				{hasAnyRole([UserRole.ValidationManager, UserRole.ValidationAdmin]) && (
					<SidebarButton
						label="Synchronisieren"
						disabled={props.isLoading || !props.vehicle.remarketing.leasmanVehicleId}
						onClick={synchronize}
					/>
				)}

				<LeasmanSyncIndicator vehicle={props.vehicle} tooLong={props.tooLong} />
			</SidebarGroup>
		</Sidebar>
	);
};

export default RemarketingSidebar;
